import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { animate, keyframes, style, transition, trigger } from '@angular/animations';
import { PerioMeasurementsBySideDraft, Tooth, ToothPerioDraft } from '@libs/ui-lib';
import { ActivatedRoute, Router } from '@angular/router';
import { NavigationService, pathParameter, TagEvent, TagPusherService, ToastModule } from '@libs/shared-services';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { first } from 'rxjs';
import { routes } from '@libs/utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PerioActiveToothFormComponent } from '../perio-active-tooth-form';
import {
  PerioExamSettingsModalComponent,
  PerioExamSettingsModalData,
  PerioExamSettingsModalPayload,
} from '../perio-exam-settings-modal/perio-exam-settings-modal.component';
import { PerioJawComponent } from '../perio-jaw';
import { PerioJawMeasurements } from '../perio-active-tooth-form/components/perio-jaw-related-measurements/perio-jaw-related-measurements.component';
import { PerioExamToolbarComponent } from '../perio-add-treatment-screen/perio-exam-toolbar/perio-exam-toolbar.component';
import { PerioTooltipToothSurface } from '../perio-add-treatment-screen/perio-exam-toolbar/perio-exam-toolbar.interface';
import { PerioFormControlService } from '../perio-active-tooth-form/services/perio-form-control/perio-form-control.service';
import { PerioExamReviewComponent } from '../perio-exam-review/perio-exam-review/perio-exam-review.component';
import { PerioExamDoneComponent } from '../perio-exam-done';
import { TargetIndexMovement } from '../perio-active-tooth-form/components/perio-config/perio-config.vm';
import { PerioExamFeatureFlagDisabledModalComponent } from '../perio-exam-feature-flag-disabled-modal/perio-exam-feature-flag-disabled-modal.component';
import {
  PerioResetAllChangesModalComponent,
  PerioResetAllChangesModalResult,
} from '../perio-reset-all-changes-modal/perio-reset-all-changes-modal.component';
import { PerioExamIntroComponent } from '../perio-exam-intro';
import { PerioScreenComponentService } from '../perio-screen/perio-screen.component.service';
import {
  LOWER_RIGHT_START,
  UPPER_RIGHT_END,
} from '../perio-add-treatment-screen/perio-position-indicator/perio-position-indicator-constants';
import { PerioTextToSpeechService } from './services/perio-exam/perio-text-to-speech.service';
import { PerioExamScreenService } from './services/perio-exam/perio-exam-screen.service';
import { VoicePerioService } from './services/perio-exam/voice-perio.service';
import { PerioExamHeaderComponent } from './components/perio-exam-header/perio-exam-header.component';
import { PerioExamState, PerioExamTraversalStrategy, PerioExamTraversalStrategyStep } from './services/perio-exam/models';
import { perioExamJawConfigMap } from './services/perio-exam/constants';
import { getPerioExamTraversalStrategy, getToothPositionsFilteredByExtracted } from './services/perio-exam/utils';

const VOICE_MODE_REFOCUS_DELAY_MS = 50;
const JAW_TRANSITION_ANIMATION_DURATION_MS = 300;

@Component({
  selector: 'lib-perio-exam-screen',
  imports: [
    CommonModule,
    PerioExamToolbarComponent,
    PerioActiveToothFormComponent,
    PerioJawComponent,
    PerioExamHeaderComponent,
    PerioExamReviewComponent,
    PerioExamDoneComponent,
    PerioExamIntroComponent,
    ToastModule,
  ],
  providers: [],
  templateUrl: './perio-exam-screen.component.html',
  styleUrl: './perio-exam-screen.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  animations: [
    trigger('jawSlide', [
      transition(':enter', [
        style({ transform: 'translateX(-100%)', opacity: 0, width: '0%' }),
        animate('600ms ease-in-out', style({ transform: 'translateX(0)', opacity: 1, width: '100%' })),
      ]),
      transition(':leave', [animate('600ms ease-in-out', style({ transform: 'translateX(-100%)', opacity: 0, width: '0%' }))]),
    ]),
    trigger('jawTransition', [
      transition(':enter', [style({ opacity: 0 }), animate('600ms 300ms ease-in-out', style({ opacity: 1 }))]),
      transition(':leave', [animate('600ms ease-in-out', style({ opacity: 0 }))]),
    ]),
    trigger('doneSlide', [
      transition(':enter', [
        style({ transform: 'translate(100%)', opacity: 0 }),
        animate('600ms 300ms ease-in-out', style({ transform: 'translate(0, -6rem)', opacity: 1 })),
      ]),
      transition(':leave', [animate('600ms ease-in-out', style({ transform: 'translate(100%, -6rem)', opacity: 0 }))]),
    ]),
    trigger('examIntroSlide', [
      transition(':enter', [
        style({ transform: 'translateY(100%)', opacity: 0 }),
        animate(
          '500ms 300ms ease-out',
          keyframes([style({ position: 'relative', offset: 0 }), style({ transform: 'translateY(0)', opacity: 1, offset: 1 })]),
        ),
      ]),
      transition(':leave', [
        animate(
          '500ms ease-in',
          keyframes([style({ transform: 'translateY(100%)', opacity: 0, offset: 0.5 }), style({ position: 'absolute', offset: 1 })]),
        ),
      ]),
    ]),
    trigger('leftJawTransition', [
      transition(':enter', [
        style({ opacity: 0, position: 'absolute' }),
        animate('500ms 300ms ease-in', keyframes([style({ position: 'relative', offset: 0 }), style({ opacity: 1, offset: 1 })])),
      ]),
      transition(':leave', [
        animate('500ms ease-out', keyframes([style({ opacity: 0, offset: 0.5 }), style({ position: 'absolute', offset: 1 })])),
      ]),
    ]),
    trigger('previewSlide', [
      transition(':enter', [
        style({ transform: 'translateY(100%)', opacity: 0, position: 'absolute' }),
        animate(
          '500ms 300ms ease-out',
          keyframes([style({ position: 'absolute', offset: 0 }), style({ transform: 'translateY(0)', opacity: 1, offset: 1 })]),
        ),
      ]),
      transition(':leave', [
        animate(
          '500ms ease-in',
          keyframes([style({ transform: 'translateY(100%)', opacity: 0, offset: 0.5 }), style({ position: 'absolute', offset: 1 })]),
        ),
      ]),
    ]),
    trigger('jawTransitionExit', [
      transition('* => up', [animate('300ms ease-in-out', style({ transform: 'translateY(-100%)', opacity: 0 }))]),
      transition('* => down', [animate('300ms ease-in-out', style({ transform: 'translateY(100%)', opacity: 0 }))]),
    ]),
    trigger('jawTransitionEnter', [
      transition('void => up', [
        style({ transform: 'translateY(100%)', opacity: 0 }),
        animate('300ms ease-in-out', style({ transform: 'translateY(0)', opacity: 1 })),
      ]),
      transition('void => down', [
        style({ transform: 'translateY(-100%)', opacity: 0 }),
        animate('300ms ease-in-out', style({ transform: 'translateY(0)', opacity: 1 })),
      ]),
    ]),
  ],
})
export class PerioExamScreenComponent implements OnInit, OnDestroy {
  private readonly screenService = inject(PerioExamScreenService);
  private readonly perioScreenService = inject(PerioScreenComponentService);
  private readonly formControlService = inject(PerioFormControlService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly navigationService = inject(NavigationService);
  private readonly dialog = inject(Dialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly tagPusherService = inject(TagPusherService);
  private readonly perioTextToSpeechService = inject(PerioTextToSpeechService);
  private readonly voicePerioService = inject(VoicePerioService);

  private perioExamSettingsModal: DialogRef<PerioExamSettingsModalPayload | undefined> | null = null;
  protected isReviewActive = false;

  protected readonly currentSide = computed(() => this.screenService.viewModel().currentToothPosition.side);
  protected readonly currentTooth = computed(() => this.screenService.viewModel().currentTooth);
  protected readonly currentPerioExamTeeth = computed(() => this.screenService.viewModel().teeth);
  protected readonly previousPerioExamTooth = computed(() => this.screenService.viewModel().previousToothForJawVisualisation);
  protected readonly nextPerioExamTooth = computed(() => this.screenService.viewModel().nextToothForJawVisualisation);

  // Actual traversal positions (not visualization)
  protected readonly nextToothPosition = computed(() => this.screenService.viewModel().nextToothPosition);
  protected readonly previousToothPosition = computed(() => this.screenService.viewModel().previousToothPosition);
  protected readonly teethRecord = computed(() => this.screenService.viewModel().teethRecord);
  protected readonly isFirstTooth = computed(() => this.screenService.viewModel().isFirstToothActive);
  protected readonly isLastTooth = computed(() => this.screenService.viewModel().isLastToothActive);
  protected readonly isFirstToothInExam = computed(() => this.screenService.viewModel().isFirstToothInExam);
  protected readonly isLastToothInExam = computed(() => this.screenService.viewModel().isLastToothInExam);
  protected readonly extractedTeethPositions = computed(() => this.screenService.viewModel().extractedTeethPositions);
  protected readonly missingTeethPositions = computed(() => this.screenService.viewModel().missingTeethPositions, {
    equal: (prev, curr) => prev.length === curr.length && prev.every((val, i) => val === curr[i]),
  });
  protected readonly allMissingTeethPositions = computed(() => [...this.extractedTeethPositions(), ...this.missingTeethPositions()]);
  protected readonly allTeethTouched = computed(() => this.screenService.viewModel().allTeethTouched);
  protected readonly oneToothTouched = computed(() => this.screenService.viewModel().oneToothTouched);
  protected readonly isEditing = computed(() => this.screenService.viewModel().isEditing);
  protected readonly screenServiceLoaded = computed(() => this.screenService.viewModel().loaded);
  protected readonly tooltipSurfaceList = computed(() => this.screenService.viewModel().indicatorToothSurfaceList);
  protected readonly addPerioExamFeatureFlagEnabled = computed(() => this.screenService.viewModel().addPerioExamFeatureEnabled);
  protected readonly cheatSheetOpened = computed(() => this.screenService.viewModel().cheatSheetOpened);
  protected readonly jawConfig = computed(() => this.screenService.viewModel().jawConfig);
  protected readonly profileId = computed(() => this.screenService.viewModel().profileId ?? '');

  protected readonly isCentralRecession = computed(() => this.screenService.isCentralRecession());

  // Jaw transition detection
  protected readonly isAtJawBoundaryForward = computed(() => {
    const current = this.currentTooth();
    const nextPos = this.nextToothPosition();
    const nextTooth = this.teethRecord()[nextPos.position];
    return current && nextTooth && current.jaw !== nextTooth.jaw;
  });

  protected readonly isAtJawBoundaryBackward = computed(() => {
    const current = this.currentTooth();
    const prevPos = this.previousToothPosition();
    const prevTooth = this.teethRecord()[prevPos.position];
    return current && prevTooth && current.jaw !== prevTooth.jaw;
  });

  protected readonly jawTransitionDirection = computed<'up' | 'down' | null>(() => {
    const current = this.currentTooth();
    const nextPos = this.nextToothPosition();
    const nextTooth = this.teethRecord()[nextPos.position];
    if (!current || !nextTooth || current.jaw === nextTooth.jaw) {
      return null;
    }
    // Upper → Lower: slide up, Lower → Upper: slide down
    return current.jaw === 'upper' ? 'up' : 'down';
  });

  // Animation state
  protected readonly isJawTransitioning = signal<boolean>(false);
  protected readonly jawAnimationPhase = signal<'exit' | 'enter' | null>(null);
  protected readonly jawAnimationDirection = signal<'up' | 'down' | null>(null);

  protected dialogClosed = signal<boolean>(false);
  public voiceStatus = this.perioTextToSpeechService.transcriptionStatus;
  public volumeLevel = this.perioTextToSpeechService.volumeLevel;
  public activeDeviceId = this.perioTextToSpeechService.activeDeviceId;
  private readonly subscribedToVoice = signal<boolean>(false);
  protected readonly isAiThinking = computed(() => this.voicePerioService.isAiResponseLoading());
  protected readonly shouldSlowDown = computed(() => this.voicePerioService.aiCommandQueueLength() > 1);
  protected closeCheatSheetAfterFirstToothLeft = effect(() => {
    if (!this.isFirstTooth()) {
      this.screenService.setCheatSheetOpenedFlag(false);
    }
  });

  public ngOnInit(): void {
    const originalPerioExam = this.getOriginalPerioExam();
    const draftPerioExamToEdit = this.screenService.getDraft() ?? undefined;
    const perioExamToEdit =
      originalPerioExam?.originalExamData === draftPerioExamToEdit?.originalExamData ? draftPerioExamToEdit : originalPerioExam;

    this.screenService.init(this.perioScreenService.viewModel().perioExams ? perioExamToEdit : undefined);

    // Initialize voice perio service with screen service callbacks
    this.screenService.initVoicePerioService();

    // Always skip opening the settings modal by default
    this.dialogClosed.set(true);
  }

  public ngOnDestroy(): void {
    this.perioExamSettingsModal?.close();
    // Stop transcription if it's still running
    if (this.voiceStatus() === 'running') {
      this.perioTextToSpeechService.stopTranscription();
    }
    this.voicePerioService.destroy();
    this.formControlService.reset();
  }

  public onCheatSheetToggle(): void {
    this.screenService.setCheatSheetOpenedFlag(!this.cheatSheetOpened());
  }

  public onReviewClick(): void {
    if (!this.isReviewActive) {
      this.formControlService.removePreservedDigit();
      this.tagPusherService.pushTag({
        event: TagEvent.PerioChartingReviewStarted,
      });
    }
    this.isReviewActive = !this.isReviewActive;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  public onVoiceToggle(): void {
    if (!this.subscribedToVoice()) {
      // Subscribe to userMessage$ and log the results
      this.perioTextToSpeechService.userMessage$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((message) => {
        if (message) {
          this.voicePerioService.sendVoiceCommand(message);
        }
      });
    }
    this.subscribedToVoice.set(true);

    if (this.voiceStatus() === 'configure') {
      // Start transcription when voice is activated
      this.perioTextToSpeechService.startTranscription();
      // Restore focus to the appropriate field after a brief delay
      // to ensure the voice mode UI has been updated
      setTimeout(() => {
        const currentToothPosition = this.screenService.viewModel().currentToothPosition;
        const side = currentToothPosition.side;
        const position = currentToothPosition.position;
        // Calculate reversed based on tooth position and side (same logic as perio-config)
        const reversed =
          side === 'buccal'
            ? position > UPPER_RIGHT_END && position < LOWER_RIGHT_START
            : !(position > UPPER_RIGHT_END && position < LOWER_RIGHT_START);

        // Check current input mode and focus appropriately
        const currentMode = this.formControlService.getInputMode();
        if (currentMode === 'recession') {
          // In recession mode, check if central-only and focus accordingly
          const isCentral = this.isCentralRecession();
          const activeCursor = this.formControlService.getActiveCursor();
          const surfaceToFocus = isCentral ? 'central' : activeCursor || (reversed ? 'mesial' : 'distal');
          this.formControlService.focusRecession(surfaceToFocus, reversed, 0, isCentral);
        } else {
          // In pocket depth mode, refocus last active pocket depth
          this.formControlService.refocusLastActivePocketDepth(reversed);
        }
      }, VOICE_MODE_REFOCUS_DELAY_MS);
    } else {
      // Stop transcription when voice is deactivated
      this.perioTextToSpeechService.stopTranscription();
      const audio = this.perioTextToSpeechService.getAudioChunks();
      const logs = this.voicePerioService.logs();
      this.screenService.uploadAudioForPerio({
        audio,
        logs,
      });
      // Reset voice perio service to clear any pending state
      this.voicePerioService.reset();
      // Re-initialize to set up new subscriptions on the fresh subjects
      this.screenService.initVoicePerioService();
      // Restore focus to appropriate field based on current input mode
      setTimeout(() => {
        const currentToothPosition = this.screenService.viewModel().currentToothPosition;
        const side = currentToothPosition.side;
        const position = currentToothPosition.position;
        // Calculate reversed based on tooth position and side (same logic as perio-config)
        const reversed =
          side === 'buccal'
            ? position > UPPER_RIGHT_END && position < LOWER_RIGHT_START
            : !(position > UPPER_RIGHT_END && position < LOWER_RIGHT_START);

        // Check current input mode and focus appropriately
        const currentMode = this.formControlService.getInputMode();
        if (currentMode === 'recession') {
          // In recession mode, check if central-only and focus accordingly
          const isCentral = this.isCentralRecession();
          const activeCursor = this.formControlService.getActiveCursor();
          const surfaceToFocus = isCentral ? 'central' : activeCursor || (reversed ? 'mesial' : 'distal');
          this.formControlService.focusRecession(surfaceToFocus, reversed, 0, isCentral);
        } else {
          // In pocket depth mode, refocus last active pocket depth
          this.formControlService.refocusLastActivePocketDepth(reversed);
        }
      }, VOICE_MODE_REFOCUS_DELAY_MS);
    }
  }

  public onSelectDevice(deviceId: string): void {
    this.perioTextToSpeechService.selectMicrophone(deviceId);
  }

  public onToothSidePerioDone(direction: TargetIndexMovement): void {
    if (!this.isFirstToothInExam() && direction === TargetIndexMovement.Prev) {
      // Check if we're at a jaw boundary
      if (this.isAtJawBoundaryBackward()) {
        this.startJawTransition('backward');
      } else {
        this.screenService.moveToPreviousTooth();
      }
      return;
    }
    if (!this.isLastToothInExam() && direction === TargetIndexMovement.Next) {
      // Check if we're at a jaw boundary
      if (this.isAtJawBoundaryForward()) {
        this.startJawTransition('forward');
      } else {
        this.screenService.moveToNextTooth();
      }
    }
  }

  public onPerioChange(ev: PerioMeasurementsBySideDraft): void {
    this.screenService.applyPerioSideChange(ev);
  }

  public onJawMeasurementsChange(measurements: PerioJawMeasurements): void {
    this.screenService.applyJawMeasurementChange(measurements);
  }

  public onMoveToNextTooth(): void {
    this.formControlService.removePreservedDigit();

    // Check if we're at a jaw boundary
    if (this.isAtJawBoundaryForward()) {
      this.startJawTransition('forward');
    } else {
      this.screenService.moveToNextTooth();
    }
  }

  public onMoveToPreviousTooth(): void {
    this.formControlService.removePreservedDigit();

    // Check if we're at a jaw boundary
    if (this.isAtJawBoundaryBackward()) {
      this.startJawTransition('backward');
    } else {
      this.screenService.moveToPreviousTooth();
    }
  }

  public onSwitchToLingual(): void {
    this.formControlService.removePreservedDigit();
    this.screenService.switchToLingual();
  }

  public onSwitchToFacial(): void {
    this.formControlService.removePreservedDigit();
    this.screenService.switchToFacial();
  }

  public onSwitchToOppositeJawLingual(toothNumber: number): void {
    this.formControlService.removePreservedDigit();

    // Determine animation direction based on current jaw
    const current = this.currentTooth();
    const animDirection: 'up' | 'down' = current.jaw === 'upper' ? 'up' : 'down';

    this.isJawTransitioning.set(true);
    this.jawAnimationPhase.set('exit');
    this.jawAnimationDirection.set(animDirection);

    // Wait for exit animation to complete
    setTimeout(() => {
      // Navigate to the opposite jaw tooth on lingual side
      this.screenService.navigateToToothAndSide(toothNumber, 'lingual');

      // Start enter animation
      this.jawAnimationPhase.set('enter');

      // Wait for enter animation to complete
      setTimeout(() => {
        this.isJawTransitioning.set(false);
        this.jawAnimationPhase.set(null);
        this.jawAnimationDirection.set(null);
      }, JAW_TRANSITION_ANIMATION_DURATION_MS);
    }, JAW_TRANSITION_ANIMATION_DURATION_MS);
  }

  public onToothNavigate(tooth: PerioTooltipToothSurface): void {
    this.formControlService.removePreservedDigit();

    // Check if we're crossing a jaw boundary
    const currentTooth = this.currentTooth();
    const targetTooth = this.teethRecord()[tooth.toothNumber];

    if (currentTooth && targetTooth && currentTooth.jaw !== targetTooth.jaw) {
      // Crossing jaw boundary - trigger animation
      const animDirection: 'up' | 'down' = currentTooth.jaw === 'upper' ? 'up' : 'down';

      this.isJawTransitioning.set(true);
      this.jawAnimationPhase.set('exit');
      this.jawAnimationDirection.set(animDirection);

      // Wait for exit animation to complete
      setTimeout(() => {
        // Navigate to the target tooth
        this.screenService.navigateToToothAndSide(tooth.toothNumber, tooth.side);

        // Start enter animation
        this.jawAnimationPhase.set('enter');

        // Wait for enter animation to complete
        setTimeout(() => {
          this.isJawTransitioning.set(false);
          this.jawAnimationPhase.set(null);
          this.jawAnimationDirection.set(null);
        }, JAW_TRANSITION_ANIMATION_DURATION_MS);
      }, JAW_TRANSITION_ANIMATION_DURATION_MS);
    } else {
      // Same jaw - navigate directly without animation
      this.screenService.navigateToToothAndSide(tooth.toothNumber, tooth.side);
    }
  }

  private startJawTransition(direction: 'forward' | 'backward'): void {
    // Determine animation direction based on current jaw and navigation direction
    const current = this.currentTooth();
    let animDirection: 'up' | 'down';

    if (direction === 'forward') {
      // Forward: upper → lower = slide up, lower → upper = slide down (but this doesn't happen in archWise)
      animDirection = current.jaw === 'upper' ? 'up' : 'down';
    } else {
      // Backward: lower → upper = slide down, upper → lower = slide up (but this doesn't happen in archWise)
      animDirection = current.jaw === 'lower' ? 'down' : 'up';
    }

    this.isJawTransitioning.set(true);
    this.jawAnimationPhase.set('exit');
    this.jawAnimationDirection.set(animDirection);

    // Wait for exit animation to complete
    setTimeout(() => {
      // Update state
      if (direction === 'forward') {
        this.screenService.moveToNextTooth();
      } else {
        this.screenService.moveToPreviousTooth();
      }

      // Start enter animation
      this.jawAnimationPhase.set('enter');

      // Wait for enter animation to complete
      setTimeout(() => {
        this.isJawTransitioning.set(false);
        this.jawAnimationPhase.set(null);
        this.jawAnimationDirection.set(null);
      }, JAW_TRANSITION_ANIMATION_DURATION_MS);
    }, JAW_TRANSITION_ANIMATION_DURATION_MS);
  }

  public onCancelExam(): void {
    this.navigationService.navigate('perioOnSiteForPatient', false, [
      this.route.snapshot.paramMap.get(pathParameter('siteId'))!,
      this.route.snapshot.paramMap.get(pathParameter('patientId'))!,
    ]);

    this.screenService.removeDraft();
  }

  public onResetExam(): void {
    const resetDialog = this.dialog.open<PerioResetAllChangesModalResult>(PerioResetAllChangesModalComponent);
    resetDialog.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => {
      if (res?.reset) {
        this.screenService.reset();
        this.formControlService.reset();
      }
    });

    this.router.events.pipe(first()).subscribe(() => {
      resetDialog?.close();
    });
  }

  public onExamCompleted(): void {
    const isSnapshot = this.route.snapshot.paramMap.get(pathParameter('siteId')) === routes.showcaseSiteId;
    const isEditing = !!this.route.snapshot.paramMap.get(pathParameter('date'));
    const examDate = this.route.snapshot.paramMap.get(pathParameter('date'));
    const siteId = this.route.snapshot.paramMap.get(pathParameter('siteId'));
    const patientId = this.route.snapshot.paramMap.get(pathParameter('patientId'));

    if (!this.addPerioExamFeatureFlagEnabled() && !isSnapshot) {
      const featureDisabledModal = this.dialog.open<PerioExamFeatureFlagDisabledModalComponent>(
        PerioExamFeatureFlagDisabledModalComponent,
        {
          backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
          panelClass: ['[&>*]:outline-none'],
          disableClose: true,
        },
      );

      this.router.events.pipe(first()).subscribe(() => {
        featureDisabledModal?.close();
      });
      return;
    }

    // Stop voice transcription if it's running and upload the audio
    if (this.voiceStatus() === 'running') {
      this.perioTextToSpeechService.stopTranscription();
      const audio = this.perioTextToSpeechService.getAudioChunks();
      const logs = this.voicePerioService.logs();
      this.screenService.uploadAudioForPerio({
        audio,
        logs,
      });
    }

    this.tagPusherService.pushTag({
      event: TagEvent.PerioChartingFinished,
    });

    this.screenService.completePerioExam(isEditing);
    this.navigationService.navigate('perioOnSiteForPatient', false, [siteId!, patientId!], {
      needDataRefetch: false,
      selectedPerioExamDate: examDate ?? undefined,
    });
  }

  private openSettingsModal() {
    this.perioExamSettingsModal = this.dialog.open<PerioExamSettingsModalPayload | undefined, PerioExamSettingsModalData>(
      PerioExamSettingsModalComponent,
      {
        data: {
          missingTeeth: this.missingTeethPositions(),
          extractedTeeth: this.extractedTeethPositions(),
        },
        backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
        panelClass: ['[&>*]:outline-none'],
        disableClose: true,
      },
    );
  }

  private getOriginalPerioExam(): PerioExamState | undefined {
    const examDate = this.route.snapshot.paramMap.get(pathParameter('date')) as string;
    const perioExam = this.perioScreenService.viewModel().perioExams.find((e) => e.date === Number.parseInt(examDate));

    if (perioExam) {
      const traversalStrategy = this.getInitialTraversalStrategy();
      const providerId = this.screenService.getDefaultProviderId();
      const jawConfig = perioExamJawConfigMap[traversalStrategy];
      const extractedTeethPositions = this.getExtractedTeethPositions() || [];
      const missingTeethPositions = this.getMissingTeethPositions() || [];
      const perioExamToothOrder = getPerioExamTraversalStrategy(jawConfig);
      const { toothOrder, currentToothPosition } = getToothPositionsFilteredByExtracted(
        [...missingTeethPositions, ...extractedTeethPositions],
        perioExamToothOrder,
      );
      const teethRecord = this.mapTeethToTeethRecord(perioExam!.teeth);
      const completedOrTouchedSteps = this.identifyCompletedOrTouchedSteps(teethRecord, toothOrder);

      return {
        loaded: true,
        teethRecord,
        completedSteps: completedOrTouchedSteps,
        touchedSteps: completedOrTouchedSteps,
        extractedTeethPositions,
        missingTeethPositions,
        currentToothPosition,
        traversalStrategy,
        toothOrder,
        providerId,
        cheatSheetOpened: false,
        isEditing: true,
        originalExamData: perioExam.date,
        recessionInputType: this.screenService.getDefaultRecessionInputType(),
      };
    }
    return undefined;
  }

  private mapTeethToTeethRecord(teeth?: Tooth[]): Record<number, ToothPerioDraft> {
    if (!teeth || !Array.isArray(teeth)) {
      return {};
    }

    return teeth.reduce(
      (acc: Record<number, ToothPerioDraft>, tooth: Tooth) => {
        if (tooth && tooth.position) {
          acc[tooth.position] = {
            ...tooth,
          };
        }
        return acc;
      },
      {} as Record<number, ToothPerioDraft>,
    );
  }

  private getExtractedTeethPositions(): number[] {
    return this.screenService.viewModel().extractedTeethPositions || [];
  }

  private getMissingTeethPositions(): number[] {
    return this.screenService.viewModel().missingTeethPositions || [];
  }

  private getInitialTraversalStrategy(): PerioExamTraversalStrategy {
    return this.screenService.getDefaultTraversalStrategy() ?? 'archWise';
  }

  private identifyCompletedOrTouchedSteps(
    teethRecord: Record<number, ToothPerioDraft>,
    toothOrder: PerioExamTraversalStrategyStep[],
  ): PerioExamTraversalStrategyStep[] {
    return toothOrder.filter((step) => {
      const tooth = teethRecord[step.position];
      if (!tooth?.perio?.[step.side]) {
        return false;
      }

      const sideMeasurements = tooth.perio[step.side];
      return this.isSomeSurfacePocketDepthCompleted(sideMeasurements);
    });
  }

  private isSomeSurfacePocketDepthCompleted(measurements: PerioMeasurementsBySideDraft): boolean {
    if (!measurements?.pocketDepth) {
      return false;
    }
    const pocketDepth = measurements.pocketDepth;
    return !!pocketDepth.distal || !!pocketDepth.central || !!pocketDepth.mesial;
  }
}
